import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PhotoIcon,
  LinkIcon,
  ChatBubbleLeftRightIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import { getTokenInfo, updateTokenMetadataOps, lockTokenMetadataOps } from '../api/vexforge';
import { signAndSend, ensureConnected } from '../lib/submit';

export default function EditToken() {
  const { mint } = useParams();
  const navigate = useNavigate();
  const { address: walletAddress, isConnected } = useWalletStore();
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [showLockConfirm, setShowLockConfirm] = useState(false);

  const [formData, setFormData] = useState({
    metadataUri: '',
    image: '',
    description: '',
    externalUrl: '',
    twitter: '',
    discord: '',
    telegram: '',
    github: '',
  });

  useEffect(() => {
    async function fetchToken() {
      try {
        const data = await getTokenInfo(mint);
        setToken(data);
        setFormData({
          metadataUri: data.metadata_uri || '',
          image: data.image || '',
          description: data.description || '',
          externalUrl: data.external_url || '',
          twitter: data.twitter || '',
          discord: data.discord || '',
          telegram: data.telegram || '',
          github: data.github || '',
        });
      } catch (err) {
        console.error('Failed to fetch token:', err);
        toast.error('Failed to load token');
      } finally {
        setLoading(false);
      }
    }
    if (mint) fetchToken();
  }, [mint]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isConnected) {
      await ensureConnected();
      return;
    }

    setSaving(true);
    const loadingToast = toast.loading('Approve the metadata update in VexSpark...');

    try {
      const txHash = await signAndSend(
        updateTokenMetadataOps(mint, formData),
        `Update metadata for ${token?.symbol || 'token'}`,
      );
      if (!txHash) { toast.dismiss(loadingToast); return; }

      toast.success(`Metadata update submitted (tx ${String(txHash).slice(0, 14)}…)`, { id: loadingToast });
      setTimeout(() => navigate(`/token/${mint}`), 1500);
    } catch (error) {
      console.error('Error updating metadata:', error);
      toast.error(error.message || 'Failed to update metadata', { id: loadingToast });
    } finally {
      setSaving(false);
    }
  };

  const isCreator = walletAddress && token?.creator &&
    walletAddress.toLowerCase() === token.creator.toLowerCase();

  const isLocked = token?.is_mutable === false;

  const handleLockMetadata = async () => {
    if (!isConnected) {
      await ensureConnected();
      return;
    }

    setLocking(true);
    const loadingToast = toast.loading('Approve the permanent metadata lock in VexSpark...');

    try {
      const txHash = await signAndSend(
        lockTokenMetadataOps(mint),
        `PERMANENTLY lock metadata for ${token?.symbol || 'token'}`,
      );
      if (!txHash) { toast.dismiss(loadingToast); return; }
      toast.success(`Metadata lock submitted (tx ${String(txHash).slice(0, 14)}…)`, { id: loadingToast });
      setShowLockConfirm(false);
      setTimeout(() => navigate(`/token/${mint}`), 1500);
    } catch (error) {
      console.error('Error locking metadata:', error);
      toast.error(error.message || 'Failed to lock metadata', { id: loadingToast });
    } finally {
      setLocking(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-400">Loading token...</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-white mb-2">Token Not Found</h1>
        <p className="text-gray-400">Could not load token at {mint}</p>
        <Link to="/explorer" className="text-vextaris-blue hover:text-white mt-4 inline-block">
          Back to Explorer
        </Link>
      </div>
    );
  }

  if (!isCreator) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-white mb-2">Not Authorized</h1>
        <p className="text-gray-400">Only the token creator can update metadata.</p>
        <Link to={`/token/${mint}`} className="text-vextaris-blue hover:text-white mt-4 inline-block">
          Back to Token
        </Link>
      </div>
    );
  }

  if (isLocked) {
    return (
      <div className="text-center py-16">
        <LockClosedIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Metadata Locked</h1>
        <p className="text-gray-400">This token&apos;s metadata has been permanently locked and cannot be updated.</p>
        <Link to={`/token/${mint}`} className="text-vextaris-blue hover:text-white mt-4 inline-block">
          Back to Token
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to={`/token/${mint}`}
          className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-4"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>Back to {token.symbol}</span>
        </Link>
        <div className="flex items-center space-x-3">
          {token.image ? (
            <img src={token.image} alt={token.name} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-vextaris flex items-center justify-center text-lg font-bold text-white">
              {(token.symbol || '?')[0]}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-white">Update {token.name} Metadata</h1>
            <p className="text-gray-400 text-sm font-mono">{token.symbol}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Image & Description */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
            <PhotoIcon className="w-5 h-5 text-vextaris-blue" />
            <span>Token Info</span>
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Image URL
              </label>
              <input
                type="url"
                name="image"
                value={formData.image}
                onChange={handleChange}
                placeholder="https://example.com/token-logo.png"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
              <p className="text-xs text-gray-500 mt-1">Direct link to token logo (PNG, JPG, SVG). Displayed on VexScan and VexForge.</p>
            </div>

            {formData.image && (
              <div className="flex items-center space-x-3">
                <img
                  src={formData.image}
                  alt="Preview"
                  className="w-12 h-12 rounded-full object-cover border border-vextaris-purple/30"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span className="text-xs text-gray-400">Preview</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Describe your token..."
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
          </div>
        </div>

        {/* Metadata URI */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
            <LinkIcon className="w-5 h-5 text-vextaris-blue" />
            <span>Metadata URI</span>
          </h2>

          <div>
            <input
              type="text"
              name="metadataUri"
              value={formData.metadataUri}
              onChange={handleChange}
              placeholder="ipfs://Qm... or https://metadata.example.com/token.json"
              className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">
              Off-chain JSON metadata. Optional — on-chain fields (image, description, socials) take priority.
            </p>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
            <ChatBubbleLeftRightIcon className="w-5 h-5 text-vextaris-blue" />
            <span>Social Links</span>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Website</label>
              <input
                type="url"
                name="externalUrl"
                value={formData.externalUrl}
                onChange={handleChange}
                placeholder="https://mytoken.com"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Twitter / X</label>
              <input
                type="text"
                name="twitter"
                value={formData.twitter}
                onChange={handleChange}
                placeholder="https://x.com/mytoken or @mytoken"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Discord</label>
              <input
                type="text"
                name="discord"
                value={formData.discord}
                onChange={handleChange}
                placeholder="https://discord.gg/mytoken"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Telegram</label>
              <input
                type="text"
                name="telegram"
                value={formData.telegram}
                onChange={handleChange}
                placeholder="https://t.me/mytoken"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">GitHub</label>
              <input
                type="text"
                name="github"
                value={formData.github}
                onChange={handleChange}
                placeholder="https://github.com/myproject"
                className="w-full px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Social links are stored on-chain and displayed on VexScan. Leave blank to remove.
          </p>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between">
          <Link
            to={`/token/${mint}`}
            className="text-gray-400 hover:text-white transition-colors"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-gradient-vextaris text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Update Metadata'}
          </button>
        </div>
      </form>

      {/* Lock Metadata Section */}
      <div className="mt-8 bg-red-500/5 backdrop-blur-xl rounded-2xl border border-red-500/20 p-6">
        <h2 className="text-xl font-semibold text-red-400 mb-2 flex items-center space-x-2">
          <LockClosedIcon className="w-5 h-5" />
          <span>Lock Metadata</span>
        </h2>
        <p className="text-gray-400 text-sm mb-4">
          Permanently lock this token&apos;s metadata. Once locked, no one — including the creator — can ever update the image, description, socials, or any other metadata fields. This action cannot be undone.
        </p>

        {!showLockConfirm ? (
          <button
            onClick={() => setShowLockConfirm(true)}
            className="px-6 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/20 transition-colors text-sm font-medium"
          >
            Lock Metadata Permanently
          </button>
        ) : (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 space-y-3">
            <p className="text-red-300 text-sm font-medium">
              Are you sure? This action is permanent and cannot be undone.
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleLockMetadata}
                disabled={locking}
                className="px-6 py-2 bg-red-500/20 border border-red-500/40 rounded-lg text-red-400 hover:bg-red-500/30 transition-colors text-sm font-semibold disabled:opacity-50"
              >
                {locking ? 'Locking...' : 'Yes, Lock Forever'}
              </button>
              <button
                onClick={() => setShowLockConfirm(false)}
                className="px-6 py-2 text-gray-400 hover:text-white transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
